import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Transition from "../utils/Transition";
import { FaCrown } from "react-icons/fa6";

import { logout } from "../store/app/action.js";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "./Buttons.jsx";
import CanView from "./CanView.jsx";
import { getStableFakeName } from "@/utils/fakeName.js";

function DropdownProfile({
  align,
  setImpersonationModalOpen,
  dropdownOpen,
  setDropdownOpen,
}) {
  const dispatch = useDispatch();
  

  const trigger = useRef(null);
  const dropdown = useRef(null);
  const { merchants, selectedMerchant, selectedLocation, user, isDemoUser } = useSelector(
    (state) => state.app
  );

  const hasMultiLocations = useMemo(() => {
    return merchants.length > 0 || merchants[0]?.locations.length > 1;
  }, [merchants]);

  const displayName = useMemo(() => {
    if(isDemoUser){
      const { first_name, last_name } = getStableFakeName(new Date(), user._id);
      return `${first_name} ${last_name}`;
    }
    return `${user?.first_name} ${user?.last_name}`;
  }, [user, isDemoUser]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <div className="relative inline-flex __tutorial-user-menu">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <div className="flex items-center truncate ">
          <div className="flex items-start truncate flex-col ">
            <span className="text-sm text-slate-800 dark:text-slate-100 font-medium truncate  ml-2 leading-5	">
              {displayName}
            </span>
            {merchants?.length > 1 && (
              <span className="truncate ml-2 text-sm font-medium dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-slate-200 leading-4	">
                {selectedMerchant?.name}
              </span>
            )}
            <span className="truncate ml-2 text-sm font-medium dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-slate-200 leading-4	">
              {selectedLocation?.subname || selectedLocation?.name}
              {selectedLocation && (
                <CanView permissions={"saucey_admin"}>
                  {" "}
                  ({selectedLocation?.store_id})
                </CanView>
              )}
            </span>
          </div>
          <svg
            className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400"
            viewBox="0 0 12 12"
          >
            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
          </svg>
        </div>
      </button>

      <Transition
        className={`origin-top-right z-10 absolute top-full min-w-44 bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1 ${
          align === "right" ? "right-0" : "left-0"
        }`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="pt-0.5 pb-2 px-3 mb-1 border-b border-slate-200 dark:border-slate-700">
            <div className="font-medium text-slate-800 dark:text-slate-100">
              {selectedMerchant?.name}
            </div>
            {selectedLocation && selectedMerchant.locations.length > 0 && (
              <div className="text-xs text-slate-500 dark:text-slate-400 italic">
                {selectedLocation?.subname || selectedLocation?.name}
              </div>
            )}
            {hasMultiLocations && (
              <div className={"mt-2"}>
                <PrimaryButton
                  onClick={() => {
                    dispatch({
                      type: "SET_LOCATION_SELECTOR_OPENED",
                      payload: true,
                    });
                  }}
                  size={"xs"}
                >
                  Change
                </PrimaryButton>
              </div>
            )}
          </div>
          <ul>
            <li>
              <Link
                className="font-medium text-sm text-theme-button-primary hover:text-theme-button-primary-hover flex items-center justify-between py-1 px-3 w-full text-left"
                to="/settings/account"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                Settings
              </Link>
            </li>
            <CanView permissions="saucey_admin">
              <li>
                <button
                  className="font-medium text-sm text-theme-button-primary hover:text-theme-button-primary-hover flex items-center justify-between py-1 px-3 w-full text-left"
                  onClick={() => {
                    setImpersonationModalOpen(true);
                    setDropdownOpen(false);
                  }}
                >
                  <span className="flex items-center">
                    Impersonate <FaCrown className="ml-1 mr-1 w-3 h-3" />
                  </span>
                  <span className="flex gap-1">
                    <kbd className="inline-block px-1.5 py-0.5 rounded border text-xs font-mono text-slate-500 border-slate-300 dark:text-slate-400 dark:border-slate-600">
                      Ctrl
                    </kbd>
                    <kbd className="inline-block px-1.5 py-0.5 rounded border text-xs font-mono text-slate-500 border-slate-300 dark:text-slate-400 dark:border-slate-600">
                      I
                    </kbd>
                  </span>
                </button>
              </li>
            </CanView>
            <li>
              <Link
                className="font-medium text-sm text-theme-button-primary hover:text-theme-button-primary-hover flex items-center justify-between py-1 px-3 w-full text-left"
                onClick={() => dispatch(logout())}
              >
                Sign Out
              </Link>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
  );
}

export default DropdownProfile;
